import { Body, Button, Divider, Heading, List } from '@hover/blueprint';
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
} from '@hover/blueprint/chakra';
import { space } from '@hover/blueprint/foundation';
import React, { useContext, useRef } from 'react';

import { PopoverColorButton } from 'src/modules/designStudio/components/DesignMenu/ColorPicker/PopoverColorButton';
import { UIKind } from 'src/modules/designStudio/components/DesignStudio/UILayer/types';
import { uiLayerContext } from 'src/modules/designStudio/providers';
import { useTranslate } from 'src/shared/hooks';
import { TranslationNamespace } from 'src/shared/hooks/useTranslate';

interface ExpandHeaderProps {
  groups: string[];
  groupLabelColors: { [key: string]: string };
  selectedGroup: string;
  setColorGroup: (group: string) => void;
  isOpen: boolean;
  onClose: () => void;
  onOpen?: () => void;
  onClick?: () => void;
}

const LandscapeExpandHeader = ({
  groups,
  selectedGroup,
  groupLabelColors,
  setColorGroup,
  isOpen,
  onClose,
  onOpen,
  onClick,
}: ExpandHeaderProps) => {
  const initialFocusRef = useRef(null);
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);

  return (
    <Popover
      gutter={parseInt(space['800'], 10)}
      initialFocusRef={initialFocusRef}
      isOpen={isOpen}
      onClose={onClose}
      onOpen={onOpen}
      placement="right"
    >
      <PopoverTrigger>
        <Button
          fill="minimal"
          fontWeight="600"
          marginTop="100"
          onClick={onClick}
          shape="box"
          size="small"
          style={{ border: 'none', boxShadow: 'none' }}
        >
          {t('menu.colorPicker.showAllCategories')}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        backgroundColor="neutral.backgroundDark"
        border="none"
        width="200px"
      >
        <PopoverArrow />
        <PopoverHeader marginBottom={0} paddingBottom={0}>
          <Heading size={300}>
            {t('menu.colorPicker.allCategoriesTitle')}
          </Heading>
          <Heading color="neutral.textLight" size={200}>
            {selectedGroup}
          </Heading>
        </PopoverHeader>
        <PopoverBody
          marginTop={0}
          paddingLeft={0}
          paddingRight={0}
          paddingTop={0}
        >
          <Divider
            borderColor="neutral.200"
            marginBottom={200}
            marginTop={200}
            orientation="horizontal"
          />
          <List paddingLeft={300} paddingRight={300}>
            {groups.map(group => (
              <PopoverColorButton
                key={group}
                label={group}
                labelColor={groupLabelColors[group]}
                onClick={() => setColorGroup(group)}
                ref={group === selectedGroup ? initialFocusRef : undefined}
                selected={group === selectedGroup}
              >
                {group}
              </PopoverColorButton>
            ))}
          </List>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

const MobileExpandHeader = ({ groups }: { groups: string[] }) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  return (
    <Body
      color="neutral.600"
      marginBlockEnd={0}
      marginBlockStart={100}
      marginBottom="500"
      minWidth="72px"
      textAlign="right"
      width="28"
    >{`${groups.length} ${t('menu.colorPicker.allCategoriesCount')}`}</Body>
  );
};

const ExpandHeader = ({ ...props }: ExpandHeaderProps) => {
  const { kind } = useContext(uiLayerContext);
  if (kind === UIKind.LANDSCAPE) {
    return <LandscapeExpandHeader {...props} />;
  }

  return <MobileExpandHeader {...props} />;
};

export { ExpandHeader };
